<script>
	import { fly } from "svelte/transition";
	import Waypoint from "svelte-waypoint";
	import Topic from "./Topic.svelte";
	import { formatInt, formatDay } from "../../../utils";
	import { allBerlinStats, points } from "../data";
	import { selected, selectedTopicId } from "../stores";
	import participantsIcon from "../icons/participants.svg";
	import filterOffIcon from "../icons/filter_off.svg";
	import locationOffIcon from "../icons/location_off.svg";
	import { platform } from "../../../components/MediaQuerySsr.svelte";
	import { onMount, afterUpdate } from "svelte";

	export let tooltip;
	export let animateFly = false;
	export let data;
	export let title;
	export let isSelected;
	export let closeCbk = (d) => {};

	const DEFAULT_NUM_TOPICS_SHOWN = 5;
	const DEFAULT_NUM_DEMOS_SHOWN = 20;
	const NUM_DEMOS_INCREMENT = 50;

	let topicsList;
	let numTopicsShown = DEFAULT_NUM_TOPICS_SHOWN;
	let demosList;
	let demoIds;
	let demoCount;
	let numDemosShown = DEFAULT_NUM_DEMOS_SHOWN;
	let scrollContainer;
	let topicsSectionHeight;

	const getTopicsDetails = () => {
		if (data) {
			const { id, centerLat, centerLng, participants, demoIds, total, searchTerm, searchLat, searchLng, ...topics } =
				data;

			if (topics && Object.keys(topics).length > 0) {
				const participantsLookup = typeof participants === "string" ? JSON.parse(participants) : participants;

				topicsList = Object.entries(topics)
					.map(([id, count]) => ({ id, count, participants: participantsLookup[id] }))
					.filter(({ id }) => !id.includes("---") && id !== "unassigned" && id !== "umwelt und tierschutz") // TF: filtering "umwelt und tierschutz" is a hotfix because of data issues
					.sort((a, b) => (a.participants < b.participants ? 1 : -1));

				return;
			}
		}
		topicsList = undefined;
	};

	const getDemosDetails = () => {
		if (data) {
			if (data.demoIds) {
				demoIds = typeof data.demoIds === "string" ? JSON.parse(data.demoIds) : data.demoIds;

				if ($selectedTopicId) {
					// for performance, when a topic is selected, pre-filter points list before matching each demo from demoIds list
					const filteredPoints = $points.filter(({ topics }) => topics?.includes($selectedTopicId));
					demosList = demoIds
						.map((id) => filteredPoints.find(({ unique_id }) => id === unique_id))
						.filter((demo) => !!demo);

					demoCount = demosList.length;
					demosList = demosList.slice(0, numDemosShown);
				} else {
					demosList = demoIds.slice(0, numDemosShown).map((id) => $points.find(({ unique_id }) => id === unique_id));

					demoCount = demoIds.length;
				}
			} else {
				demoIds = undefined;
				demosList = (
					$selectedTopicId ? $points.filter(({ topics }) => topics?.includes($selectedTopicId)) : $points
				).sort((a, b) => (a.participants_actually > b.participants_actually ? -1 : 1));

				demoCount = demosList.length;
				demosList = demosList.slice(0, numDemosShown);
			}
		} else {
			demoIds = undefined;
			demosList = undefined;
		}
	};

	const resetNumShownCounts = () => {
		numTopicsShown = DEFAULT_NUM_TOPICS_SHOWN;
		numDemosShown = DEFAULT_NUM_DEMOS_SHOWN;
	};

	const formatNumber = formatInt();

	const formatDate = (date) => {
		return formatDay(new Date(date));
	};
	const formatParticipants = (participants) => {
		return formatInt()(participants);
	};

	const scrollToTop = () => {
		scrollContainer?.scrollTo({ top: 0, left: 0, behavior: "instant" });
	};

	let bottomEnd = false;
	const checkForGradient = () => {
		if (!scrollContainer) return;
		const difference = scrollContainer.scrollTop + scrollContainer.offsetHeight - scrollContainer.scrollHeight;
		bottomEnd = Math.abs(difference) < 1 || scrollContainer.scrollHeight <= scrollContainer.offsetHeight;
	};

	onMount(() => {
		checkForGradient();
	});
	afterUpdate(() => {
		checkForGradient();
	});

	// $: data, checkForGradient();
	$: data, numTopicsShown, getTopicsDetails();
	$: data, $selectedTopicId, numDemosShown, getDemosDetails();
	$: data, resetNumShownCounts();
	$: showMoreDemosButtonVisible = data && (!demoIds || demoIds.length > numDemosShown);
	$: $selectedTopicId, data, scrollToTop();

	$: console.log(topicsList);
</script>

{#if $platform !== "mobile" || isSelected}
	<div
		class="tooltip"
		on:click
		on:mouseover
		on:focus={() => {}}
		on:blur={() => {}}
		on:mouseout
		transition:fly={{ y: 100, duration: animateFly ? 400 : 0 }}
		bind:this={tooltip}
	>
		<div class="tooltip-top">
			<div class="tooltip-top__inner">
				<div class="header">
					{#if title}
						<span class="header__title">{formatNumber(demoCount)} {demoCount === 1 ? "Demo" : "Demos"}</span> um
						<span class="header__title">{title}</span>
					{:else if data?.id}
						<span class="header__title">{formatNumber(demoCount)} {demoCount === 1 ? "Demo" : "Demos"}</span>
					{:else}
						<span class="header__title"
							>{formatNumber(demoCount)} {demoCount === 1 ? "Demo" : "Demos"} in ganz Berlin
						</span>
					{/if}
				</div>
				{#if isSelected}
					<div class="button-close" on:click={closeCbk} />
				{/if}
			</div>
			<div class="rule" />
		</div>
		<div
			class="tooltip-main"
			bind:this={scrollContainer}
			on:scroll={() => {
				checkForGradient();
			}}
		>
			{#if $selectedTopicId}
				<!-- <div class="selected-topic">
				<Topic
					colored
					outlined
					topicId={$selectedTopicId}
					onClose={() => {
						$selectedTopicId = undefined;
					}}
				/>
			</div> -->
			{:else if !data.unique_id}
				{#if topicsList?.length}
					<div class="section animate-height" style:height={`${topicsSectionHeight}px`}>
						<div bind:clientHeight={topicsSectionHeight}>
							<div class="section__content">
								<div class="table-header">
									<div class="table-header__item">
										<h5 class="section__title">WICHTIGSTE THEMEN</h5>
									</div>
									<div class="table-header__item align-end">Demos</div>
									<div class="table-header__item align-end fixed-width">Teilnehm.</div>
								</div>
								{#each topicsList.slice(0, numTopicsShown) as { id, count, participants }, i (id)}
									<div class="topic">
										<div class="rank">{i + 1}</div>
										<div class="title"><Topic topicId={id} /></div>
										<div class="count">{count}</div>
										<div class="participants fixed-width">
											{#if participants}
												<span>{formatParticipants(participants)}<img src={participantsIcon} /></span>
											{:else}
												k.A.
											{/if}
										</div>
									</div>
								{/each}
							</div>
							{#if topicsList.length > numTopicsShown}
								<div class="topics-show-more" on:click={() => (numTopicsShown = topicsList.length)}>
									{#if topicsList.length - numTopicsShown === 1}
										+ 1 weiteres Thema anzeigen
									{:else}
										+ {formatNumber(topicsList.length - numTopicsShown)} weitere Themen anzeigen
									{/if}
								</div>
							{/if}
						</div>
					</div>
				{/if}
			{/if}

			<!-- {#if data.unique_id} -->
			<!-- <h5 class="section__title">AUGEWÄHLTE DEMO</h5>
			<div class="demo">
				{#each topicsList as topicId}
					<Topic
						small
						colored
						{topicId}
						onClick={() => {
							$selectedTopicId = topicId;
						}}
					/>
				{/each}
				<div class="demo__details">
					<div class="date">{formatDate(data.date)}</div>
					<div class="participants">{data.participants_actually}</div>
				</div>
				<div class="title">{data.title}</div>
			</div> -->
			{#if demosList?.length > 0}
				<div class="section">
					<div class="section__content">
						<div class="table-header">
							<div class="table-header__item">
								<h5 class="section__title">{demosList[0].participants_actually ? "GRÖSSTE DEMOS" : "DEMOS"}</h5>
							</div>
							{#if demosList[0].participants_actually}
								<div class="table-header__item align-end">Teilnehm.</div>
							{/if}
						</div>
						{#each demosList as { unique_id, date, title, participants_actually, topics }, i (unique_id)}
							<div class="demo-list-item__details">
								<div class="rank">{i + 1}</div>
								<div class="date-topic">
									<span class="date">{formatDate(date)}</span>
									{#if topics}
										{#each topics as topicId}
											{#if !topicId.includes("---") || topicId === $selectedTopicId}
												<Topic
													small
													colored
													hoverTitle="nach Thema filtern"
													{topicId}
													onClick={() => {
														$selectedTopicId = topicId;
													}}
												/>
											{/if}
										{/each}
									{/if}
								</div>
								<div class="participants">
									{#if participants_actually}
										<span>{formatParticipants(participants_actually)}<img src={participantsIcon} /></span>
									{/if}
								</div>
							</div>
							<div class="demo-list-item__title">
								<div />
								<div class="title">{title}</div>
							</div>
						{/each}
					</div>
					{#if showMoreDemosButtonVisible}
						<Waypoint
							throttle="100"
							offset="200"
							once={false}
							on:enter={() => {
								numDemosShown = numDemosShown + NUM_DEMOS_INCREMENT;
							}}
						/>
					{/if}
				</div>
			{:else}
				<div class="no-demos">
					<div class="no-demos__text">
						<div class="no-demos__text__icon">!</div>
						<div class="no-demos__text__text">
							Hier fanden keine Demos {#if $selectedTopicId}zum gewählten Thema {/if}statt.
						</div>
					</div>
					<div class="no-demos__buttons">
						{#if $selectedTopicId}
							<div
								class="no-demos__button filter"
								on:click={() => {
									$selectedTopicId = undefined;
								}}
							>
								<div class="no-demos__button__left">
									<img src={filterOffIcon} />
									<span>Filter löschen</span>
								</div>
								<div class="no-demos__button__main">
									{formatNumber(demoIds?.length || 0)} Demos an diesem Ort zeigen
								</div>
							</div>
						{/if}
						<div class="no-demos__button" on:click={closeCbk}>
							<div class="no-demos__button__left">
								<img src={locationOffIcon} />
								<span>Ort löschen</span>
							</div>
							<div class="no-demos__button__main">
								{formatNumber($selectedTopicId ? $allBerlinStats[$selectedTopicId] : $points.length)} Demos in ganz Berlin
								anzeigen
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
		<div class="gradient bottom" class:hidden={bottomEnd} />
	</div>
{/if}

<style lang="scss">
	$tooltip-offset: 35vh;
	$padding-top: 10px;
	$padding-right: 6px;
	$padding-bottom: 10px;
	$padding-left: 9px;

	.tooltip {
		padding: $padding-top $padding-right $padding-bottom $padding-left;
		position: absolute;
		top: 0px;
		left: 5px;
		font-size: 16px;
		line-height: 120%;
		box-shadow: 0px 0px 15px rgba(255, 255, 255, 0.12);
		width: 330px;
		box-sizing: border-box;
		z-index: 1;
		overflow: hidden;
		border-radius: 5px;
		background: rgba($main-blue, 0.8);
		backdrop-filter: blur(10px);
		-webkit-backdrop-filter: blur(10px);
		color: #fff;
		height: 100%;
		@include scrollbar;
	}

	.gradient {
		position: absolute;
		left: -$padding-left;
		height: 20%;
		width: calc(100% + $padding-left + $padding-right);
		pointer-events: none;

		@media (max-width: $breakpoint-tablet-l) {
			height: 30%;

			&.bottom {
				background-image: linear-gradient(rgba(255, 255, 255, 0) 0%, rgba($main-blue, 0.8) 50%, $main-blue 80%);
			}
		}

		&.bottom {
			bottom: 0px;
			background-image: linear-gradient(rgba(255, 255, 255, 0) 0%, rgba($main-blue, 0.8) 50%, $main-blue 80%);
		}

		&.hidden {
			display: none;
		}
	}

	@media (max-width: $breakpoint-tablet-l) {
		$padding-top: 10px;
		$padding-right: 15px;
		$padding-bottom: 10px;
		$padding-left: 15px;
		$tooltip-offset: 22vh;
		$tooltip-maxheight: 260px;

		.tooltip {
			// background: rgba($main-blue, 0.95);
			padding: $padding-top $padding-right $padding-bottom $padding-left;
			position: absolute;
			top: calc(100% - max(#{$tooltip-offset}, #{$tooltip-maxheight}) - 28px);
			width: 100%;
			left: 0;
			right: 0;
			border-radius: 10px;
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
			max-height: max(#{$tooltip-offset}, #{$tooltip-maxheight});
			max-width: 60vw;
			left: 50%;
			transform: translateX(-50%);
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		$tooltip-offset: 22vh;
		$tooltip-maxheight: 260px;

		.tooltip {
			max-width: 380px !important;
			top: calc(100% - max(#{$tooltip-offset}, #{$tooltip-maxheight}));
		}
	}

	.rule {
		height: 1px;
		background: rgba(255, 255, 255, 0.3);
		margin: 4px 0;
	}

	.tooltip-top__inner {
		display: flex;
		justify-content: space-between;
		// align-items: ;
	}

	.tooltip-main {
		overflow-y: scroll;
		overflow-x: hidden;
		height: calc(100% - 40px);
		position: relative;
	}

	.button-close {
		cursor: pointer;
		background-position: center center;
		background-image: url(../assets/images/icons/icon_close.svg);
		background-repeat: no-repeat;
		width: 24px;
		height: 24px;
		min-width: 30px;
		background-size: contain;
	}

	.button-back {
		cursor: pointer;
		background-position: center center;
		background-image: url(../assets/images/icons/icon_back.svg);
		background-repeat: no-repeat;
		width: 24px;
		height: 24px;
		background-size: contain;
		margin-right: 5px;
	}

	.show-more {
		color: #fff;
		opacity: 0.8;
		font-size: 13px;
		font-weight: 400;
		letter-spacing: 0.13px;
		cursor: pointer;
	}

	.header {
		color: #f3f3f390;
		font-size: 16px;
		font-weight: $bold;
		line-height: 24px; /* 133.333% */
		// height: 24px;

		span.header__title {
			color: #f3f3f3;
		}
	}

	.section {
		margin: 10px 0 20px;

		&__content {
			width: 100%;
		}

		&__title {
			color: #fff;
			font-size: 14px;
			font-style: normal;
			line-height: normal;
			font-weight: $medium;
			letter-spacing: 0.75px;
			text-transform: uppercase;
			opacity: 1;
			margin: 0;
		}

		&__footnote {
			color: #9b93b8;
			font-size: 12px;
			font-style: italic;
			font-weight: 400;
			line-height: 120%; /* 14.4px */
			letter-spacing: 0.12px;
			padding: 10px 0;
		}
	}

	.animate-height {
		transition: height 120ms linear;
	}

	.table-header {
		display: flex;
		&__item {
			padding: 3px 2px 10px;
			color: #aaa2c7;
			font-size: 11px;
			font-style: normal;
			font-weight: 400;
			line-height: 120%;
			letter-spacing: 0.44px;
			opacity: 0.8;
			text-transform: uppercase;
			text-align: start;

			&:first-child {
				flex-grow: 1;
			}

			&.align-end {
				text-align: end;
			}
		}
	}

	.rank,
	.title,
	.count,
	.participants,
	.date-topic {
		padding: 3px 2px;
		color: #fff;
		font-size: 13px;
		font-style: normal;
		font-weight: normal;
		line-height: 120%; /* 15.6px */
		letter-spacing: 0.13px;
		opacity: 0.8;
		vertical-align: top;
	}

	.topic {
		.count,
		.participants {
			flex-shrink: 0;
		}
	}

	.topic,
	.demo-list-item__details,
	.demo-list-item__title {
		width: 100%;
		display: flex;
	}

	.rank {
		width: 20px;
	}

	.topic > div,
	.demo-list-item__title > div {
		border-bottom: 1px solid rgba(#cdcdcd, 0.2);
		padding-bottom: 7px;

		&.title {
			flex-grow: 1;
		}
	}

	.demo-list-item__title > .title {
		margin-left: 25px;
	}

	.demo-list-item__details > div {
		padding-top: 9px;

		&.title,
		&.date-topic {
			flex-grow: 1;
		}

		&.date-topic {
			padding-top: 7px;
		}
	}

	.rank {
		color: #f3f3f3;
		opacity: 0.65;
		flex-shrink: 0;
	}

	.count {
		padding-right: 7px;
	}

	.count,
	.participants {
		text-align: end;
	}

	.participants {
		font-weight: $medium;
		opacity: 1;
		padding-right: 0;

		& > span {
			display: inline-flex;
			align-items: center;
		}
	}

	.fixed-width {
		width: 67px;
	}

	.date {
		color: #877fa4;
		font-size: 13px;
		font-weight: $medium;
		line-height: 120%;
		letter-spacing: 0.13px;
	}

	.demo {
		background: rgba(255, 255, 255, 0.1);
		box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
		padding: 7px;
		margin-top: 15px;

		&__details {
			display: flex;
			justify-content: space-between;
			margin: 7px 0;
		}
	}

	.topics-show-more,
	.demos-show-more {
		border-radius: 36px;
		background: rgba(255, 255, 255, 0.2);
		cursor: pointer;
		color: #fff;
		font-weight: $medium;
		font-size: 13px;
		padding: 2px 7px;
		margin: 10px 0;
		display: inline-block;

		&:hover {
			opacity: 0.7;
		}
	}

	.selected-topic {
		padding: 10px 0;
	}

	.no-demos {
		border-radius: 4px;
		background: rgba(255, 255, 255, 0.1);
		box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
		padding: 10px;

		&__text {
			color: #fff;
			opacity: 0.7;
			font-size: 14px;
			font-style: normal;
			font-weight: $medium;
			line-height: normal;
			margin-bottom: 10px;
			display: flex;
			gap: 20px;
			align-items: center;

			&__icon {
				font-size: 34px;
				padding-left: 10px;
			}
		}

		&__button {
			$padding: 10px;
			display: inline-flex;
			align-items: center;
			justify-content: space-between;
			gap: 7px;
			color: #fff;
			border-radius: 0 0 4px 4px;
			border: 1px solid rgba(255, 255, 255, 0.5);
			opacity: 0.78;
			background: rgba(255, 255, 255, 0.2);
			padding: $padding;
			cursor: pointer;
			width: calc(100% - #{2 * $padding});

			&:first-child {
				border-radius: 4px;
			}

			&:hover {
				opacity: 0.6;
			}

			&.filter {
				color: #ff4e4e;
				border-color: #ff4e4e;
				background: rgba(255, 78, 78, 0.2);
				border-radius: 4px 4px 0px 0px;
			}

			&__left {
				display: inline-flex;
				align-items: center;
				gap: 5px;
				font-size: 14px;
				font-style: normal;
				font-weight: $medium;
				line-height: 110%;
				white-space: nowrap;
			}

			&__main {
				font-size: 12px;
				line-height: 110%;
				text-align: right;
			}
		}
	}

	@media (max-width: $breakpoint-tablet-l) {
		.header {
			position: relative;
			padding-right: 20px;
		}
	}
</style>
